import {
  SearchExamEventExamineesDocument,
  SearchExamineeStatusesInExamEventDocument,
  useOverrideExamEventExamineeEmailMutation,
} from '@exam-center/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Form,
  FormButton,
  FormTextField,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';

import {
  type OverrideExamEventExamineeEmailFormInput,
  OverrideExamEventExamineeEmailFormSchema,
} from './schema';

interface OverrideExamEventExamineeEmailModalProps {
  open: boolean;
  onClose: () => void;
  examEventId: string;
  examEventExamineeId: string;
}

export default function OverrideExamEventExamineeEmailModal(
  props: Readonly<OverrideExamEventExamineeEmailModalProps>,
) {
  const { open: isOpen, onClose, examEventId, examEventExamineeId } = props;

  const { showSnackbar } = useSnackbar();

  const formContext = useForm<OverrideExamEventExamineeEmailFormInput>({
    resolver: zodResolver(OverrideExamEventExamineeEmailFormSchema),
    defaultValues: {
      email: '',
    },
  });

  const { handleSubmit, reset, formState } = formContext;

  const [overrideExamEventExamineeEmail, { loading }] = useOverrideExamEventExamineeEmailMutation({
    awaitRefetchQueries: true,
    refetchQueries: [SearchExamineeStatusesInExamEventDocument, SearchExamEventExamineesDocument],
  });

  const handleOverrideEmail = async (data: OverrideExamEventExamineeEmailFormInput) => {
    await overrideExamEventExamineeEmail({
      variables: {
        overrideExamEventExamineeEmailInput: {
          examEventId,
          examEventExamineeId,
          email: data.email,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'แก้ไขอีเมลผู้เข้าสอบเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'เกิดข้อผิดพลาดในการแก้ไขอีเมลผู้เข้าสอบ',
          variant: 'error',
        });
      },
    });
  };

  const handleResetForm = () => {
    reset();
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='sm'
        disableEscapeKeyDown={formState.isSubmitting}
        slotProps={{
          paper: {
            component: Form,
            onSubmit: handleSubmit(handleOverrideEmail),
          },
          transition: {
            onExited: handleResetForm,
          },
        }}
      >
        <DialogTitle onClose={onClose} loading={formState.isSubmitting}>
          แก้ไขอีเมลผู้เข้าสอบ
        </DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(4) }}
          dividers
        >
          <FormTextField name='email' label='อีเมล' fullWidth placeholder='กรุณากรอกอีเมล' />
        </DialogContent>
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={loading}
          >
            ยกเลิก
          </Button>
          <FormButton size='large' type='submit' label='บันทึกข้อมูล' />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
